import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon, PencilIcon, TrashIcon } from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { mcpApi } from '@/api/mcp.api';
import { mcpClassApi } from '@/api/mcpClass.api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs } from '@/components/ui/tabs';
import { formatTimestamp } from '@/utils/utils';
import { MCP_TRANSPORT_LABELS } from '@/pages/mcp/mcp-constants';
import type { MCP } from '@/proto/agentplatform/v1/mcp_pb';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="p-1 hover:bg-accent rounded transition-colors"
      title="复制"
    >
      {copied ? (
        <svg
          className="w-3.5 h-3.5 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg
          className="w-3.5 h-3.5 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )}
    </button>
  );
}

function InfoItem({
  label,
  children,
  copyable,
}: {
  label: string;
  children: React.ReactNode;
  copyable?: string;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">{children}</div>
        {copyable && <CopyButton text={copyable} />}
      </div>
    </div>
  );
}

type TabId = 'overview' | 'tools';

function OverviewTab({ mcp }: { mcp: MCP }) {
  const [revealHeaders, setRevealHeaders] = useState(false);

  const { data: classData } = useQuery({
    queryKey: ['mcp-class', mcp.classId],
    queryFn: () => mcpClassApi.getMCPClass(mcp.classId),
    enabled: !!mcp.classId,
  });

  const headers = mcp.headers || {};
  const headerEntries = Object.entries(headers);

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">基本信息</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="名称">
              <span className="font-medium">{mcp.name}</span>
            </InfoItem>
            <InfoItem label="创建时间">
              <span className="text-sm">{formatTimestamp(mcp.createdAt)}</span>
            </InfoItem>
          </div>
          <InfoItem label="描述">
            <span className="text-sm">{mcp.description || '暂无描述'}</span>
          </InfoItem>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="MCP ID" copyable={mcp.id}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                {mcp.id}
              </code>
            </InfoItem>
            {mcp.classId && (
              <InfoItem label="来源 MCP 模版">
                <Link
                  to={`/plugins/mcps/classes/${mcp.classId}`}
                  className="text-xs text-primary hover:underline font-mono"
                >
                  {classData?.name || mcp.classId}
                </Link>
              </InfoItem>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">连接配置</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="Server URL" copyable={mcp.serverUrl}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                {mcp.serverUrl || '-'}
              </code>
            </InfoItem>
            <InfoItem label="传输方式">
              <Badge variant="secondary">{MCP_TRANSPORT_LABELS[mcp.transport] || '-'}</Badge>
            </InfoItem>
            <InfoItem label="超时（秒）">
              <span className="text-sm">
                {mcp.timeoutSeconds > 0 ? `${mcp.timeoutSeconds}s` : '默认'}
              </span>
            </InfoItem>
            <InfoItem label="TLS 校验">
              {mcp.skipTlsVerify ? (
                <Badge variant="destructive" className="text-[10px]">
                  跳过校验
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">
                  默认校验
                </Badge>
              )}
            </InfoItem>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Headers
              </span>
              {headerEntries.length > 0 && (
                <button
                  type="button"
                  onClick={() => setRevealHeaders((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {revealHeaders ? '隐藏值' : '显示值'}
                </button>
              )}
            </div>
            {headerEntries.length === 0 ? (
              <p className="text-xs text-muted-foreground">暂无 header</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium w-1/3">名称</th>
                      <th className="px-3 py-2 text-left font-medium">值</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {headerEntries.map(([k, v]) => (
                      <tr key={k}>
                        <td className="px-3 py-2 font-mono text-xs">{k}</td>
                        <td className="px-3 py-2 font-mono text-xs break-all">
                          {revealHeaders ? v : v ? '•'.repeat(Math.min(v.length, 24)) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ToolsTab({ mcpId }: { mcpId: string }) {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [expandedTool, setExpandedTool] = useState<string | null>(null);
  const [showRawJson, setShowRawJson] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['mcp-tools', mcpId],
    queryFn: () => mcpApi.listMCPTools(mcpId),
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">工具列表</CardTitle>
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <LoaderIcon className="h-3.5 w-3.5 animate-spin" />
            加载中...
          </span>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-4">
          <div className="flex flex-col items-center gap-2">
            <svg
              className="h-10 w-10 text-destructive/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <p className="text-sm text-destructive">工具加载失败</p>
            <p className="text-xs text-muted-foreground max-w-md">{fullErrorText(error)}</p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              重试
            </Button>
          </div>
          <div className="rounded-md border bg-muted/30 px-4 py-2.5 text-left max-w-md mx-auto">
            <p className="text-xs font-medium text-muted-foreground mb-1">排查建议</p>
            <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
              <li>点击「测试连接」验证 MCP 服务是否可达</li>
              <li>检查 Server URL 是否正确、服务是否在线</li>
              <li>确认传输方式（SSE / Streamable HTTP）与服务端一致</li>
              <li>查看服务端日志确认工具列表是否正常返回</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  }

  const tools = data?.data || [];
  if (tools.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-2">
          <p className="text-sm text-muted-foreground">该 MCP 暂无工具</p>
          <p className="text-xs text-muted-foreground">请确认 MCP 服务已正确暴露工具列表</p>
        </CardContent>
      </Card>
    );
  }

  const totalPages = Math.max(1, Math.ceil(tools.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedTools = tools.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const renderSchemaFields = (schemaStr: string) => {
    try {
      const schema = JSON.parse(schemaStr);
      const properties = schema.properties || {};
      const required: string[] = schema.required || [];

      return Object.entries(properties).map(([key, prop]: [string, any]) => (
        <div key={key} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1">
          <code className="text-xs font-medium text-primary">{key}</code>
          {required.includes(key) && <span className="text-[10px] text-destructive">必填</span>}
          {prop.type && (
            <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {prop.type}
            </span>
          )}
          {prop.description && (
            <span className="text-xs text-muted-foreground">{prop.description}</span>
          )}
        </div>
      ));
    } catch {
      return (
        <pre className="text-[11px] text-muted-foreground whitespace-pre-wrap break-all">
          {schemaStr}
        </pre>
      );
    }
  };

  const prettyJson = (schemaStr: string) => {
    try {
      return JSON.stringify(JSON.parse(schemaStr), null, 2);
    } catch {
      return schemaStr;
    }
  };

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`已复制${label}`);
  };

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base">工具列表</CardTitle>
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          {isFetching && <LoaderIcon className="h-3.5 w-3.5 animate-spin" />}共 {tools.length}{' '}
          个工具
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="divide-y border rounded-md">
          {pagedTools.map((tool) => (
            <div key={tool.name} className="px-4 py-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-sm font-medium truncate">{tool.name}</span>
                    <button
                      type="button"
                      title="复制工具名称"
                      onClick={() => copyText(tool.name, '工具名称')}
                      className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                      </svg>
                    </button>
                  </div>
                  {tool.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {tool.description}
                    </p>
                  )}
                </div>
                {tool.inputSchema && (
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedTool(expandedTool === tool.name ? null : tool.name);
                      setShowRawJson(false);
                    }}
                    className="shrink-0 text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    {expandedTool === tool.name ? '收起参数' : '查看参数'}
                  </button>
                )}
              </div>
              {tool.inputSchema && expandedTool === tool.name && (
                <div className="mt-2 rounded-md border bg-muted/30">
                  <div className="flex items-center justify-between px-3 py-1.5 border-b">
                    <button
                      type="button"
                      onClick={() => setShowRawJson(!showRawJson)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {showRawJson ? (
                          <>
                            <path d="m6 9 6 6 6-6" />
                          </>
                        ) : (
                          <>
                            <path d="m9 18 6-6-6-6" />
                          </>
                        )}
                      </svg>
                      {showRawJson ? '参数说明' : '原始 JSON'}
                    </button>
                    {showRawJson && (
                      <button
                        type="button"
                        onClick={() => copyText(prettyJson(tool.inputSchema), 'Schema')}
                        className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                        复制
                      </button>
                    )}
                  </div>
                  {showRawJson ? (
                    <pre className="px-3 py-2 text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                      {prettyJson(tool.inputSchema)}
                    </pre>
                  ) : (
                    <div className="px-3 py-2">{renderSchemaFields(tool.inputSchema)}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">
              第 {currentPage} / {totalPages} 页
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                上一页
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                下一页
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function MCPInstanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pingResult, setPingResult] = useState<{
    ok: boolean;
    error: string;
    latencyMs: bigint;
  } | null>(null);
  const [pingFadingOut, setPingFadingOut] = useState(false);

  useEffect(() => {
    if (!pingResult) return;
    setPingFadingOut(false);
    const fadeTimer = window.setTimeout(() => setPingFadingOut(true), 2700);
    const hideTimer = window.setTimeout(() => setPingResult(null), 3000);
    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(hideTimer);
    };
  }, [pingResult]);

  const { data: mcp, isLoading } = useQuery({
    queryKey: ['mcp', id],
    queryFn: () => mcpApi.getMCP(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => mcpApi.deleteMCP(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcps'] });
      navigate('/plugins/mcps?tab=instance');
    },
  });

  const testMutation = useMutation({
    mutationFn: () => mcpApi.testMCPConnection(id!),
    onSuccess: (resp) => {
      setPingResult({ ok: resp.ok, error: resp.error, latencyMs: resp.latencyMs });
      if (resp.ok) {
        queryClient.invalidateQueries({ queryKey: ['mcp-tools', id] });
        toast.success('连接成功，已刷新工具列表');
      }
    },
    onError: (err) => {
      toast.error(`测试连接失败：${fullErrorText(err)}`);
    },
  });

  const tabs = [
    { id: 'overview', label: '概览' },
    { id: 'tools', label: '工具' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (!mcp) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <h3 className="text-lg font-semibold mb-2">MCP 实例不存在</h3>
          <Button variant="outline" onClick={() => navigate('/plugins/mcps?tab=instance')}>
            返回列表
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/plugins/mcps?tab=instance')}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{mcp.name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{mcp.description || '暂无描述'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={testMutation.isPending}
            onClick={() => testMutation.mutate()}
            className="gap-1.5"
          >
            {testMutation.isPending ? (
              <>
                <LoaderIcon className="h-3.5 w-3.5 animate-spin" />
                测试中...
              </>
            ) : (
              '测试连接'
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/plugins/mcps/instances/${id}/edit`)}
          >
            <PencilIcon className="mr-1.5 h-3.5 w-3.5" />
            编辑
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            className="text-destructive hover:text-destructive"
          >
            <TrashIcon className="mr-1.5 h-3.5 w-3.5" />
            删除
          </Button>
        </div>
      </div>

      {pingResult && (
        <div className="fixed inset-x-0 top-6 z-50 flex justify-center pointer-events-none">
          <div
            className={`pointer-events-auto px-6 py-3 rounded-md shadow-lg text-sm border min-w-[420px] max-w-[640px] text-center transition-opacity duration-300 ${
              pingFadingOut ? 'opacity-0' : 'opacity-100'
            } ${
              pingResult.ok
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-destructive/10 border-destructive/20 text-destructive'
            }`}
          >
            {pingResult.ok
              ? `连接成功（${pingResult.latencyMs}ms）`
              : `连接失败：${pingResult.error || '未知错误'}`}
          </div>
        </div>
      )}

      <Tabs tabs={tabs} activeTab={activeTab} onChange={(tid) => setActiveTab(tid as TabId)} />

      <div className="animate-in fade-in duration-300">
        {activeTab === 'overview' && <OverviewTab mcp={mcp} />}
        {activeTab === 'tools' && <ToolsTab mcpId={mcp.id} />}
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除 MCP 实例「{mcp.name}」吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
